import styled, { keyframes } from 'styled-components';

// Picks a value per theme. Only two variants exist, so
// styled-components generates just two classes.
const theme =
    (dark, light) =>
    ({ $isDarkMode }) =>
        $isDarkMode ? dark : light;

// Barely-there drift so the dots feel suspended in the
// glass rather than printed on it.
const drift = keyframes`
    0%, 100% {
        transform: translate3d(0, 0, 0);
    }
    50% {
        transform: translate3d(var(--dx), var(--dy), 0);
    }
`;

export const CardWrapper = styled.div`
    display: inline-flex;
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 32px;
    cursor: pointer;

    /* Liquid Glass material: heavy blur, boosted colour */
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);

    @media (max-width: 768px) {
        backdrop-filter: blur(16px) saturate(160%);
        -webkit-backdrop-filter: blur(16px) saturate(160%);
    }

    /* Clear body with a faint tint that is strongest at
       the top, where the light comes from. */
    background-color: ${({ $CARD_BACKGROUND_COLOR }) =>
        $CARD_BACKGROUND_COLOR};
    background-image: ${theme(
        `linear-gradient(
            172deg,
            rgba(255, 255, 255, 0.08) 0%,
            rgba(255, 255, 255, 0.02) 50%,
            rgba(255, 255, 255, 0) 100%
        )`,
        `linear-gradient(
            172deg,
            rgba(255, 255, 255, 0.5) 0%,
            rgba(255, 255, 255, 0.15) 50%,
            rgba(255, 255, 255, 0.05) 100%
        )`
    )};
    border: 1px solid
        ${({ $CARD_BORDER_COLOR }) => $CARD_BORDER_COLOR};

    /* Soft floating shadow + the refraction band just
       inside the edge: bright where the light enters at
       the top-left, a dimmer echo at the bottom-right. */
    box-shadow: ${theme(
        `0 24px 48px -20px rgba(0, 0, 0, 0.65),
            0 1px 2px rgba(0, 0, 0, 0.35),
            inset 1px 1px 12px -3px rgba(255, 255, 255, 0.28),
            inset -1px -1px 12px -3px rgba(255, 255, 255, 0.14),
            inset 0 0 0 1px rgba(255, 255, 255, 0.05)`,
        `0 24px 48px -20px rgba(40, 40, 80, 0.28),
            0 1px 2px rgba(40, 40, 80, 0.08),
            0 0 0 1px rgba(255, 255, 255, 0.4),
            inset 1px 1px 12px -3px rgba(255, 255, 255, 0.95),
            inset -1px -1px 12px -3px rgba(255, 255, 255, 0.7),
            inset 0 0 0 1px rgba(255, 255, 255, 0.5)`
    )};

    transition: transform 0.35s
            cubic-bezier(0.2, 0.8, 0.2, 1),
        box-shadow 0.35s ease, background-color 0.35s ease;

    /* Glass rim: a thin ring whose brightness follows the
       light, peaking at the top-left corner with a second,
       softer glint at the bottom-right. */
    @supports (
        (-webkit-mask-composite: xor) or
            (mask-composite: exclude)
    ) {
        &::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: -1;
            pointer-events: none;
            border-radius: inherit;
            padding: 1.5px;
            background: ${theme(
                `conic-gradient(
                    from 0deg,
                    rgba(255, 255, 255, 0.25) 0deg,
                    rgba(255, 255, 255, 0.05) 50deg,
                    rgba(255, 255, 255, 0.08) 100deg,
                    rgba(255, 255, 255, 0.7) 135deg,
                    rgba(255, 255, 255, 0.08) 175deg,
                    rgba(255, 255, 255, 0.03) 230deg,
                    rgba(255, 255, 255, 0.2) 280deg,
                    rgba(255, 255, 255, 0.95) 315deg,
                    rgba(255, 255, 255, 0.25) 360deg
                )`,
                `conic-gradient(
                    from 0deg,
                    rgba(255, 255, 255, 0.6) 0deg,
                    rgba(255, 255, 255, 0.3) 50deg,
                    rgba(255, 255, 255, 0.35) 100deg,
                    rgba(255, 255, 255, 1) 135deg,
                    rgba(255, 255, 255, 0.35) 175deg,
                    rgba(255, 255, 255, 0.25) 230deg,
                    rgba(255, 255, 255, 0.5) 280deg,
                    rgba(255, 255, 255, 1) 315deg,
                    rgba(255, 255, 255, 0.6) 360deg
                )`
            )};
            -webkit-mask: linear-gradient(#000 0 0)
                    content-box,
                linear-gradient(#000 0 0);
            -webkit-mask-composite: xor;
            mask: linear-gradient(#000 0 0) content-box,
                linear-gradient(#000 0 0);
            mask-composite: exclude;
        }
    }

    /* Specular glare: a soft reflection along the top edge
       and a faint one in the opposite corner. */
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: -2;
        pointer-events: none;
        border-radius: inherit;
        background: ${theme(
            `radial-gradient(
                    120% 55% at 50% -15%,
                    rgba(255, 255, 255, 0.22) 0%,
                    rgba(255, 255, 255, 0.05) 45%,
                    rgba(255, 255, 255, 0) 70%
                ),
                radial-gradient(
                    70% 45% at 105% 110%,
                    rgba(255, 255, 255, 0.1) 0%,
                    rgba(255, 255, 255, 0) 60%
                )`,
            `radial-gradient(
                    120% 55% at 50% -15%,
                    rgba(255, 255, 255, 0.75) 0%,
                    rgba(255, 255, 255, 0.2) 45%,
                    rgba(255, 255, 255, 0) 70%
                ),
                radial-gradient(
                    70% 45% at 105% 110%,
                    rgba(255, 255, 255, 0.5) 0%,
                    rgba(255, 255, 255, 0) 60%
                )`
        )};
        opacity: 0.9;
        transition: opacity 0.35s ease;
    }

    &:hover {
        transform: translateY(-6px);
        background-color: ${theme(
            'rgba(255, 255, 255, 0.07)',
            'rgba(255, 255, 255, 0.55)'
        )};
        box-shadow: ${theme(
            `0 32px 56px -22px rgba(0, 0, 0, 0.75),
                0 2px 4px rgba(0, 0, 0, 0.35),
                inset 1px 1px 14px -3px rgba(255, 255, 255, 0.4),
                inset -1px -1px 14px -3px rgba(255, 255, 255, 0.2),
                inset 0 0 0 1px rgba(255, 255, 255, 0.08)`,
            `0 32px 56px -22px rgba(40, 40, 80, 0.35),
                0 2px 4px rgba(40, 40, 80, 0.08),
                0 0 0 1px rgba(255, 255, 255, 0.6),
                inset 1px 1px 14px -3px rgba(255, 255, 255, 1),
                inset -1px -1px 14px -3px rgba(255, 255, 255, 0.85),
                inset 0 0 0 1px rgba(255, 255, 255, 0.7)`
        )};

        &::after {
            opacity: 1;
        }
    }

    &:active {
        transform: translateY(-2px) scale(0.985);
    }
`;

// Holds the micro dots: tiny bright inclusions in the glass.
// Sits behind the card content (negative z-index inside the
// card's own stacking context) and never intercepts clicks.
export const Dots = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    pointer-events: none;
    border-radius: inherit;
    overflow: hidden;
`;

export const Dot = styled.span.attrs(({ $dot }) => ({
    style: {
        width: $dot.size,
        height: $dot.size,
        left: `${$dot.left}%`,
        top: `${$dot.top}%`,
        opacity: $dot.opacity,
        animationDuration: `${$dot.duration}s`,
        animationDelay: `${$dot.delay}s`,
        '--dx': `${$dot.driftX}px`,
        '--dy': `${$dot.driftY}px`,
    },
}))`
    position: absolute;
    display: block;
    border-radius: 50%;
    background: ${theme(
        'rgba(255, 255, 255, 0.95)',
        'rgba(255, 255, 255, 1)'
    )};
    box-shadow: ${theme(
        '0 0 2px rgba(255, 255, 255, 0.55)',
        `0 0 0 0.5px rgba(0, 0, 0, 0.18),
            0 0 2px rgba(255, 255, 255, 0.9)`
    )};
    animation: ${drift} ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }

    /* Fewer dots on small screens */
    @media (max-width: 768px) {
        &:nth-child(n + 13) {
            display: none;
        }
    }
`;
